import Cookies from "js-cookie";

type Integration = "Google Tag Manager";

type Integrations = { [key in Integration]: boolean };

type ConsentPreferences = {
  version: string;
  userPreferences: boolean;
  integrations: Integrations;
};

export const preferencesCookieName = "tracking-preferences";
const version = "1.0";

const onIntegrationsChangeCallbacks: Array<
  (integrations: Integrations) => void
> = [];

const defaultPreferences: ConsentPreferences = {
  version,
  userPreferences: false,
  integrations: {
    "Google Tag Manager": false,
  },
};

function setDefaultPreferences() {
  Cookies.set(preferencesCookieName, JSON.stringify(defaultPreferences), {
    expires: 365,
  });
}

function getPreferences(): ConsentPreferences {
  try {
    const preferences = Cookies.get(preferencesCookieName);
    if (preferences) {
      return JSON.parse(preferences);
    }
  } catch (error) {
    console.error(error);
    setDefaultPreferences();
  }
  return defaultPreferences;
}

const ConsentManager = {
  integrations(): Integrations {
    return getPreferences().integrations;
  },

  clearPreferences() {
    Cookies.remove(preferencesCookieName);
  },

  setIntegrations(
    integrations: Integrations,
    userPreferences = false,
    override = false,
  ): void {
    if (this.didUserSetPreferences() && !userPreferences && !override) {
      // we shouldn't override user preferences
      return;
    }

    const preferences = getPreferences();
    preferences.userPreferences = userPreferences;

    let didPreferencesChange = false;
    let shouldReloadWindow = false;
    Object.keys(integrations).forEach((_k) => {
      // TODO: (shlomnissan) figure out why this cast is necessary
      const key = _k as Integration;

      if (preferences.integrations[key] !== integrations[key]) {
        didPreferencesChange = true;
      }
      preferences.integrations[key] = integrations[key];
      if (!integrations[key]) {
        // if any integration is being disabled, we must force a page reload
        shouldReloadWindow = true;
      }
    });

    Cookies.set(preferencesCookieName, JSON.stringify(preferences), {
      expires: 365,
    });
    if (didPreferencesChange) {
      if (shouldReloadWindow) {
        window.location.reload();
      } else {
        onIntegrationsChangeCallbacks.forEach((fn) =>
          fn(preferences.integrations),
        );
      }
    }
  },

  didUserSetPreferences() {
    return getPreferences().userPreferences;
  },

  onIntegrationsChange(callback: (integration: Integrations) => void) {
    onIntegrationsChangeCallbacks.push(callback);
  },
};

export default ConsentManager;
